import { Slot } from '@radix-ui/react-slot';
import * as React from 'react';

import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../lib/utils';

const buttonVariants = cva(
  // Deliberately no `aria-disabled:*` here. `aria-disabled` has several existing consumers
  // with their own dimmed look (e.g. `FiltersDrawer`'s `CheckRow`), and `cn()` cannot dedupe
  // across variant prefixes — tailwind-merge files `opacity-40` and `aria-disabled:opacity-60`
  // under different groups, so both survive and the prefixed one wins on attribute-selector
  // specificity. A base-level rule would therefore silently overwrite callers instead of
  // being overridden by them. Components that want the locked look opt in locally; see
  // `LockedAction`. (`disabled:*` below is safe: it is the long-standing base contract.)
  'text-blueyGrey rounded-lg inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90',
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        outline: 'border border-input bg-background hover:bg-accent hover:text-accent-foreground shadow-sm',
        secondary:
          'bg-white text-neutral-500 border border-neutral-100 shadow-sm hover:shadow-none hover:text-green-400 disabled:opacity-50 disabled:pointer-events-none disabled:border-neutral-300 disabled:text-neutral-500 disabled:bg-neutral-100 focus-visible:ring-green-400 focus-visible:ring-offset-2',
        ghost: 'hover:bg-neutral-50 hover:text-neutral-400',
        link: 'text-primary underline-offset-4 hover:underline',
        danger: 'bg-red-500 text-white hover:bg-red-600',
        success: 'bg-green-400 text-white hover:bg-green-400/90',
        brand: 'bg-green-400 text-white',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-9 rounded-md px-3',
        lg: 'h-11 rounded-md px-8',
        icon: 'h-10 w-10',
        iconSm: 'size-[30px] p-0',
        iconXs: 'size-7 p-0',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
